import React, { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import { LeadsList } from "./LeadsList";
import { LeadProfile } from "./LeadProfile";
import { selectSelectedLead } from "src/redux/selectors/experiences/leadsAndReports";
import { clearSelectedLead, selectLead } from "src/redux/actions/experiences/leadsAndReports";

interface LeadsTabProps {
  storyboardId: string;
  storyboardName?: string;
  storyboardType?: string;
}


const useProfileRouteGuard = (
  isProfileRoute: boolean,
  selected: ReturnType<typeof selectSelectedLead>,
) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const pathSegments = location.pathname.split("/");
  const lastSegment = pathSegments[pathSegments.length - 1];
  const profileIdFromPath = lastSegment !== "profile" && /^[a-f0-9]{24}$/i.test(lastSegment)
    ? lastSegment
    : null;
  const canHydrate = isProfileRoute && Boolean(profileIdFromPath);
  const shouldHydrate = canHydrate && selected?.profileId !== profileIdFromPath;

  useEffect(() => {
    if (shouldHydrate && profileIdFromPath) {
      dispatch(selectLead({ profileId: profileIdFromPath, data: {} }));
    }
  }, [shouldHydrate, profileIdFromPath, dispatch]);

  useEffect(() => {
    if (isProfileRoute && !selected && !canHydrate) {
      navigate(location.pathname.replace(/\/profile.*$/, ""), { replace: true });
    }
  }, [isProfileRoute, selected, canHydrate]); // eslint-disable-line react-hooks/exhaustive-deps

  return { isHydrating: isProfileRoute && !selected && canHydrate };
};

export const LeadsTab: React.FC<LeadsTabProps> = ({ storyboardId, storyboardName, storyboardType }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const selected = useSelector(selectSelectedLead);

  const isProfileRoute = location.pathname.includes("/leads/profile");

  const { isHydrating } = useProfileRouteGuard(isProfileRoute, selected);

  const navigatedFor = useRef<string | null>(
    !isProfileRoute && selected ? selected.profileId : null,
  );

  useEffect(() => {
    if (!selected) {
      navigatedFor.current = null;
      return;
    }
    if (!isProfileRoute && navigatedFor.current !== selected.profileId) {
      navigatedFor.current = selected.profileId;
      navigate(`profile/${selected.profileId}${location.search}`, { relative: "path" });
    }
  }, [selected]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isProfileRoute && selected) dispatch(clearSelectedLead());
  }, [isProfileRoute]); // eslint-disable-line react-hooks/exhaustive-deps

  if (isProfileRoute && selected) {
    return (
      <LeadProfile
        storyboardId={storyboardId}
        storyboardName={storyboardName}
        storyboardType={storyboardType}
      />
    );
  }

  if (isHydrating) return null;

  return <LeadsList storyboardId={storyboardId} />;
};
